import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, X, Send, Loader2 } from 'lucide-react';
import { buildLeadPayload, formatPhone, sendLead } from '@/lib/leadTracking';

const WHATSAPP_NUMBER = '5551983007447';
const BUBBLE_DELAY_MS = 20_000;
const DISMISS_KEY = 'pps_chat_bubble_dismissed';
const EASE_OUT = [0.23, 1, 0.32, 1] as const;

const inputClass =
  'w-full rounded-xl border border-gray-300 bg-white px-3.5 py-3 text-[15px] text-gray-900 caret-[#8e3a87] placeholder:text-gray-500 outline-none transition-[border-color,box-shadow] duration-150 focus:border-[#8e3a87] focus:ring-4 focus:ring-[#8e3a87]/15 aria-[invalid=true]:border-red-400';

const readDismissed = () => {
  try {
    return sessionStorage.getItem(DISMISS_KEY) === '1';
  } catch {
    return false;
  }
};

const buildWhatsAppUrl = (name: string, message: string) => {
  const text = `Olá! Meu nome é ${name}. Vim pelo site do Método PPS e tenho uma dúvida:\n\n${message}`;
  return `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(text)}`;
};

interface ChatWidgetProps {
  /** Sobe o botão quando existe uma barra fixa embaixo (CTA mobile). */
  raised?: boolean;
  /** Esconde tudo enquanto outro modal (checkout) está aberto. */
  hidden?: boolean;
}

/**
 * Chat de dúvidas: depois de 20s mostra um balão oferecendo ajuda; ao abrir,
 * coleta nome, WhatsApp e a dúvida, envia ao mesmo webhook do checkout e
 * leva a pessoa ao WhatsApp com a mensagem já escrita.
 */
const ChatWidget: React.FC<ChatWidgetProps> = ({ raised = false, hidden = false }) => {
  const [open, setOpen] = useState(false);
  const [bubble, setBubble] = useState(false);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState<{ field: 'name' | 'phone' | 'message'; text: string } | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const nameRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (readDismissed()) return;
    const id = window.setTimeout(() => setBubble(true), BUBBLE_DELAY_MS);
    return () => window.clearTimeout(id);
  }, []);

  useEffect(() => {
    if (!open) return;
    setBubble(false);
    const focus = window.setTimeout(() => nameRef.current?.focus(), 220);
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    window.addEventListener('keydown', onKey);
    return () => {
      window.clearTimeout(focus);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const dismissBubble = () => {
    setBubble(false);
    try {
      sessionStorage.setItem(DISMISS_KEY, '1');
    } catch {
      // sem storage, o balão só não volta nesta visita
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;

    const cleanName = name.trim();
    const phoneDigits = phone.replace(/\D/g, '');
    const cleanMessage = message.trim();

    if (cleanName.length < 2) return setError({ field: 'name', text: 'Digite seu nome.' });
    if (phoneDigits.length < 10) return setError({ field: 'phone', text: 'Digite seu WhatsApp com DDD.' });
    if (cleanMessage.length < 3) return setError({ field: 'message', text: 'Conte qual é a sua dúvida.' });

    setError(null);
    setSubmitting(true);

    const whatsappUrl = buildWhatsAppUrl(cleanName, cleanMessage);
    const payload = await buildLeadPayload({
      name: cleanName,
      email: '',
      phone: phoneDigits,
      product_id: 'duvida-whatsapp',
      product_name: 'Dúvida pelo chat do site',
      product_price: '',
      checkout_url: whatsappUrl,
      message: cleanMessage,
      lead_source: 'chat_whatsapp',
    });

    try {
      (window as any).fbq?.('track', 'Contact');
    } catch {
      // pixel indisponível
    }

    // Espera o envio por no máximo 2s; o keepalive segura o restante durante o redirect
    await Promise.race([sendLead(payload), new Promise((r) => setTimeout(r, 2000))]);
    window.location.href = whatsappUrl;
  };

  if (hidden) return null;

  const offset = raised ? 'bottom-24 sm:bottom-6' : 'bottom-5 sm:bottom-6';

  return (
    <div className={`fixed right-4 z-40 flex flex-col items-end gap-3 sm:right-6 ${offset} transition-[bottom] duration-200`}>
      <AnimatePresence>
        {open && (
          <motion.div
            key="panel"
            role="dialog"
            aria-modal="false"
            aria-labelledby="chat-title"
            initial={{ opacity: 0, scale: 0.96, y: 8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.97, y: 6, transition: { duration: 0.15 } }}
            transition={{ duration: 0.22, ease: EASE_OUT }}
            style={{ transformOrigin: 'bottom right' }}
            className="w-[calc(100vw-2rem)] max-w-[360px] overflow-hidden rounded-2xl bg-white shadow-[0_24px_60px_rgba(42,23,68,0.28)] ring-1 ring-[#5f448f]/10"
          >
            <div className="flex items-center gap-3 bg-[#2a1744] px-4 py-3.5 text-white">
              <img src="/icon-192.png" alt="" className="h-10 w-10 rounded-full" />
              <div className="min-w-0 flex-1">
                <p id="chat-title" className="font-bold leading-tight">Time Método PPS</p>
                <p className="text-xs text-white/70">Respondemos pelo WhatsApp</p>
              </div>
              <button
                onClick={() => setOpen(false)}
                aria-label="Fechar chat"
                className="rounded-lg p-1.5 text-white/80 transition-colors hover:bg-white/10 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/50"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="bg-[#faf7fc] px-4 pt-4">
              <p className="w-fit max-w-[85%] rounded-2xl rounded-tl-md bg-white px-3.5 py-2.5 text-[15px] leading-snug text-gray-800 shadow-sm">
                Oi! Como podemos te ajudar? Deixe seu nome, seu WhatsApp e a sua dúvida, e a conversa continua por lá.
              </p>
            </div>

            <form onSubmit={handleSubmit} noValidate className="space-y-3 bg-[#faf7fc] p-4">
              <div>
                <label htmlFor="chat-name" className="sr-only">Seu nome</label>
                <input
                  id="chat-name"
                  ref={nameRef}
                  className={inputClass}
                  placeholder="Seu nome"
                  autoComplete="name"
                  value={name}
                  aria-invalid={error?.field === 'name'}
                  onChange={(e) => setName(e.target.value)}
                />
              </div>
              <div>
                <label htmlFor="chat-phone" className="sr-only">Seu WhatsApp com DDD</label>
                <input
                  id="chat-phone"
                  type="tel"
                  inputMode="tel"
                  className={inputClass}
                  placeholder="Seu WhatsApp com DDD"
                  autoComplete="tel-national"
                  value={phone}
                  aria-invalid={error?.field === 'phone'}
                  onChange={(e) => setPhone(formatPhone(e.target.value))}
                />
              </div>
              <div>
                <label htmlFor="chat-message" className="sr-only">Sua dúvida</label>
                <textarea
                  id="chat-message"
                  rows={3}
                  className={`${inputClass} resize-none`}
                  placeholder="Escreva sua dúvida"
                  value={message}
                  aria-invalid={error?.field === 'message'}
                  onChange={(e) => setMessage(e.target.value)}
                />
              </div>

              {error && <p role="alert" className="rounded-lg bg-red-50 px-3 py-2 text-sm font-medium text-red-700">{error.text}</p>}

              <button
                type="submit"
                disabled={submitting}
                className="flex w-full items-center justify-center gap-2 rounded-xl bg-[#1a6f71] px-5 py-3.5 font-extrabold text-white transition-[background-color,transform] duration-150 ease-out hover:bg-[#145a5c] active:scale-[0.97] focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[#1a6f71]/30 disabled:cursor-wait disabled:opacity-90"
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin" /> Abrindo o WhatsApp...
                  </>
                ) : (
                  <>
                    Continuar no WhatsApp <Send className="h-4 w-4" />
                  </>
                )}
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {bubble && !open && (
          <motion.div
            key="bubble"
            role="status"
            initial={{ opacity: 0, scale: 0.94, y: 6 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, transition: { duration: 0.15 } }}
            transition={{ duration: 0.25, ease: EASE_OUT }}
            style={{ transformOrigin: 'bottom right' }}
            className="relative flex max-w-[250px] items-start gap-2 rounded-2xl rounded-br-md bg-white py-3 pl-4 pr-9 text-[15px] leading-snug text-gray-800 shadow-[0_12px_30px_rgba(42,23,68,0.2)] ring-1 ring-[#5f448f]/10"
          >
            <button onClick={() => setOpen(true)} className="text-left focus-visible:outline-none">
              Oi! Posso te ajudar com alguma dúvida?
            </button>
            <button
              onClick={dismissBubble}
              aria-label="Fechar mensagem"
              className="absolute right-1.5 top-1.5 rounded-full p-1 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#5f448f]/40"
            >
              <X className="h-4 w-4" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      <button
        onClick={() => setOpen((v) => !v)}
        aria-label={open ? 'Fechar chat' : 'Abrir chat de dúvidas'}
        aria-expanded={open}
        className="relative flex h-14 w-14 items-center justify-center rounded-full bg-[#1a6f71] text-white shadow-[0_10px_25px_rgba(26,111,113,0.4)] transition-[background-color,transform] duration-150 ease-out hover:bg-[#145a5c] active:scale-[0.94] focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[#1a6f71]/30"
      >
        <AnimatePresence initial={false} mode="wait">
          <motion.span
            key={open ? 'close' : 'chat'}
            initial={{ opacity: 0, rotate: -45, scale: 0.8 }}
            animate={{ opacity: 1, rotate: 0, scale: 1 }}
            exit={{ opacity: 0, rotate: 45, scale: 0.8 }}
            transition={{ duration: 0.15, ease: EASE_OUT }}
            className="flex"
          >
            {open ? <X className="h-6 w-6" /> : <MessageCircle className="h-6 w-6" />}
          </motion.span>
        </AnimatePresence>
        {bubble && !open && <span className="absolute right-0.5 top-0.5 h-3.5 w-3.5 rounded-full border-2 border-white bg-[#c46bbb]" aria-hidden="true" />}
      </button>
    </div>
  );
};

export default ChatWidget;
